import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle, Bug, Key, GitBranch, GlobeLock,
  Newspaper, ArrowRight, Boxes, CreditCard
} from "lucide-react";
import { Link } from "react-router-dom";

const featureIcons = [Key, Bug, GlobeLock, AlertTriangle, GitBranch, Newspaper, Boxes, CreditCard];

const FeaturesSection = () => {
  const { t } = useTranslation("home");
  const featureItems = featureIcons.map((Icon, idx) => ({
    icon: <Icon size={20} />,
    title: t(`features.items.${idx}.title`),
    description: t(`features.items.${idx}.description`),
  }));

  return (
    <section className="relative bg-[#020617] text-white py-32 px-6 lg:px-12 overflow-hidden font-sans">
      
      {/* --- Section Divider / Light Beam --- */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-400/50 to-transparent z-20"></div>
      
      {/* FIX 1: Responsive Width for the Top Beam (w-[90%] on mobile, w-[500px] on desktop) */}
      <div className="absolute -top-10 left-1/4 -translate-x-1/2 w-[90%] md:w-[500px] h-[100px] bg-indigo-600/30 blur-[60px] rounded-full pointer-events-none z-10"></div>
      <div className="absolute -top-12 left-1/4 -translate-x-1/2 w-[200px] md:w-[250px] h-[60px] bg-blue-500/20 blur-[40px] rounded-full pointer-events-none z-10"></div>
      
      {/* Background Decor */}
      <div className="absolute inset-0 pointer-events-none">
        {/* FIX 2: Responsive Width/Height for the Background Orb */}
        {/* Changed from fixed w-[600px] to responsive w-[300px] md:w-[600px] */}
        <div className="absolute -top-20 left-0 md:left-1/4 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-blue-600/5 blur-[80px] md:blur-[120px] rounded-full opacity-30"></div>
        
        <div 
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#020617]/50 to-[#020617]"></div>
      </div>

      {/* Dashboard backdrop — half-visible decorative image anchored to the right of the section.
          Masked to fade out toward the left so it doesn't compete with feature cards. */}
      <div
        className="hidden lg:block absolute top-1/2 -translate-y-1/2 right-0 w-[60%] xl:w-[55%] pointer-events-none z-0 opacity-[0.28]"
        style={{
          maskImage: "linear-gradient(to left, black 30%, transparent 95%)",
          WebkitMaskImage: "linear-gradient(to left, black 30%, transparent 95%)",
        }}
        aria-hidden="true"
      >
        <img
          src="https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/a9547490-4307-426e-41f7-06c7da2d9900/w=2000,q=85,format=auto"
          srcSet="https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/a9547490-4307-426e-41f7-06c7da2d9900/w=2000,q=85,format=auto 1x, https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/2253c18e-9306-438e-9175-275280f44500/w=4000,q=85,format=auto 2x"
          alt=""
          className="block w-full h-auto translate-x-[18%] xl:translate-x-[12%] blur-[1.5px]"
          width={2000}
          height={1333}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="max-w-3xl mb-20">
          <motion.span 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500/10 to-pink-500/10 border border-indigo-500/20 mb-6"
          >
            <span className="bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent text-m font-bold uppercase tracking-widest">
              {t("features.badge")}
            </span>
          </motion.span>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold tracking-tight mb-8 leading-[1.1]"
          >
            {t("features.title")} <br />
            <span className="text-slate-500">{t("features.titleHighlight")}</span>
          </motion.h2>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-slate-300 text-lg leading-relaxed max-w-2xl"
          >
            {t("features.description")}
          </motion.p>
        </div>

        {/* Features 4-column grid — compact cards, scans as an index of capabilities. */}
        {/* The dashboard mockup sits as a faded/blurred backdrop on the right (see below the grid for the absolute element). */}
        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {featureItems.map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: idx * 0.05 }}
              className="group relative bg-slate-900/80 backdrop-blur-md border border-white/10 p-5 rounded-2xl transition-all duration-300 hover:border-blue-500/50 hover:bg-slate-900 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-900/20"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-transparent to-blue-500/0 group-hover:from-blue-500/10 group-hover:to-transparent rounded-2xl transition-all duration-500" />

              <div className="relative z-10">
                <div className="w-10 h-10 rounded-lg bg-slate-800 border border-white/10 flex items-center justify-center text-slate-300 group-hover:bg-blue-500/20 group-hover:border-blue-500/50 group-hover:text-blue-400 transition-all duration-300 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-[15px] font-semibold text-slate-100 group-hover:text-white transition-colors mb-1.5 leading-tight">
                  {feature.title}
                </h3>
                <p className="text-[12.5px] text-slate-400 leading-snug group-hover:text-slate-300 transition-colors">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Platform Tour CTA — centered below the grid */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/platform-tour"
            className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20 hover:border-indigo-500/60 text-indigo-300 hover:text-white text-sm font-bold uppercase tracking-wider transition-all shadow-lg shadow-indigo-900/20 no-underline"
          >
            {t("features.platformTour")}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;